/**
 * Subscription Plan Definitions
 * Single source of truth for plan features, limits, and pricing.
 * Used by both backend (worker) and frontend (web, mobile).
 */
/**
 * Entitlements per plan tier.
 * Defines which features are available for each tier.
 */
export const PLAN_ENTITLEMENTS = {
    free: new Set([
        // Booking & Calendar (all tiers)
        // Service catalog primary (all tiers)
        // Payments - Stripe Connect (all tiers)
        // Messaging - SMS & email confirmation + basic reminders (all tiers)
        // Client list & appointment history (all tiers)
        // Single provider as owner (all tiers)
        // Provider ratings (all tiers)
        'providerRatings',
        // Booking page links (all tiers)
        'bookingPageLinks',
    ]),
    solo: new Set([
        // All Free features, plus:
        'addonsAndBundles',
        'perServiceDeposits',
        'configurableCancellationPolicies',
        'automaticRefunds',
        'paymentTransactionHistory',
        'configurableReminderPolicies',
        'automatedFollowUps',
        'clientNotesAndTags',
        'clientSegmentation',
        'visitAnalytics',
        'lapsedFollowUp',
        'actionCenter',
        'reporting',
        'providerSchedules',
        'providerCapacity',
        'providerRatings',
        'loyalty',
        'riskScoring',
        'aiConcierge',
        'bookingPageLinks',
        // NOT included: sharedInbox, multipleProviders, providerInvites, providerSkills, teamScheduling, boothRentals
    ]),
    team: new Set([
        // All Solo features, plus Team-only:
        'addonsAndBundles',
        'perServiceDeposits',
        'configurableCancellationPolicies',
        'automaticRefunds',
        'paymentTransactionHistory',
        'configurableReminderPolicies',
        'automatedFollowUps',
        'clientNotesAndTags',
        'clientSegmentation',
        'visitAnalytics',
        'lapsedFollowUp',
        'actionCenter',
        'reporting',
        'providerSchedules',
        'providerCapacity',
        'providerRatings',
        'loyalty',
        'riskScoring',
        'sharedInbox',
        'multipleProviders',
        'providerInvites',
        'providerSkills',
        'teamScheduling',
        'boothRentals',
        'aiConcierge',
        'aiPhotoImport',
        'bookingPageLinks',
    ]),
};
/**
 * Plan limits (e.g. max clients, max providers).
 */
export const PLAN_LIMITS = {
    free: {
        maxClients: 20,
        maxProviders: 1,
        maxConciergeTurnsPerMonth: 0,
    },
    solo: {
        maxClients: Infinity,
        maxProviders: 1,
        maxConciergeTurnsPerMonth: 100,
    },
    team: {
        maxClients: Infinity,
        maxProviders: Infinity,
        maxConciergeTurnsPerMonth: Infinity,
    },
};
/**
 * Pricing and display metadata for plans.
 */
export const PLAN_METADATA = {
    free: {
        name: 'Free',
        price: 0,
        description: 'Basic booking for very small solo operations',
    },
    solo: {
        name: 'Solo',
        price: 29,
        description: 'Booking with CRM automation, business intelligence, and a capped AI assistant',
    },
    team: {
        name: 'Team',
        price: 69,
        description: 'Fully armed Tilly designed for a team',
    },
};
/**
 * Check if a plan tier has a specific feature.
 */
export function hasFeature(tier, feature) {
    return PLAN_ENTITLEMENTS[tier].has(feature);
}
/**
 * Get a plan limit for a tier.
 */
export function getLimit(tier, limitKey) {
    return PLAN_LIMITS[tier][limitKey];
}
/**
 * Get all features available for a tier.
 */
export function getFeaturesForTier(tier) {
    return Array.from(PLAN_ENTITLEMENTS[tier]);
}
/**
 * Get metadata (name, price) for a tier.
 */
export function getMetadata(tier) {
    return PLAN_METADATA[tier];
}
const TIER_ORDER = ['free', 'solo', 'team'];
/**
 * Get the minimum tier that has access to a feature, derived from PLAN_ENTITLEMENTS.
 * Returns null if no tier has the feature.
 */
export function getMinimumTier(feature) {
    return TIER_ORDER.find((tier) => hasFeature(tier, feature)) ?? null;
}
//# sourceMappingURL=plan.js.map